const MANDATORY_PRAYERS = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
const ENTRY_WINDOW_SECONDS = 15 * 60;

const toSeconds = (time) => {
  if (!time) return null;
  const [hours, minutes, seconds = 0] = String(time).split(":").map(Number);
  if (![hours, minutes, seconds].every(Number.isFinite)) return null;
  return hours * 3600 + minutes * 60 + seconds;
};

const formatDuration = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(safeSeconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

export const formatPrayerClock = (time) => {
  const seconds = toSeconds(time);
  if (seconds === null) return "---";
  const hours24 = Math.floor(seconds / 3600) % 24;
  const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${minutes}`;
};

export const formatPrayerClockPeriod = (time) => {
  const seconds = toSeconds(time);
  if (seconds === null) return "---";
  const period = Math.floor(seconds / 3600) % 24 >= 12 ? "PM" : "AM";
  return `${formatPrayerClock(time)} ${period}`;
};

const sceneForTime = (prayerTimes, nowSeconds) => {
  const imsak = toSeconds(prayerTimes?.imsak);
  const fajr = toSeconds(prayerTimes?.fajr);
  const syuruk = toSeconds(prayerTimes?.syuruk);
  const asr = toSeconds(prayerTimes?.asr);
  const maghrib = toSeconds(prayerTimes?.maghrib);

  if (imsak !== null && nowSeconds >= imsak && syuruk !== null && nowSeconds < syuruk) {
    return "dawn";
  }
  if (syuruk !== null && asr !== null && nowSeconds >= syuruk && nowSeconds < asr) {
    return "day";
  }
  if (asr !== null && maghrib !== null && nowSeconds >= asr && nowSeconds < maghrib) {
    return "afternoon";
  }
  if (maghrib !== null && nowSeconds >= maghrib && nowSeconds < maghrib + 45 * 60) {
    return "sunset";
  }
  if (fajr === null && nowSeconds >= 7 * 3600 && nowSeconds < 18 * 3600) return "day";
  return "night";
};

export const getPrayerHeroState = ({ prayerTimes, nextDayPrayerTimes, now = new Date() }) => {
  const nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const scene = sceneForTime(prayerTimes, nowSeconds);

  if (!prayerTimes) {
    return {
      prayer: "",
      mode: "until",
      duration: "--:--:--",
      scheduledTime: "---",
      scheduledTimePeriod: "---",
      scene,
      activePrayer: "",
    };
  }

  const today = MANDATORY_PRAYERS.map((name) => ({
    name,
    seconds: toSeconds(prayerTimes[name]),
    rawTime: prayerTimes[name],
  })).filter((item) => item.seconds !== null);

  const recent = [...today].reverse().find((item) => item.seconds <= nowSeconds);
  const elapsed = recent ? nowSeconds - recent.seconds : Number.POSITIVE_INFINITY;

  if (recent && elapsed < ENTRY_WINDOW_SECONDS) {
    return {
      prayer: recent.name,
      mode: "since",
      duration: formatDuration(elapsed),
      scheduledTime: formatPrayerClock(recent.rawTime),
      scheduledTimePeriod: formatPrayerClockPeriod(recent.rawTime),
      scene,
      activePrayer: recent.name,
    };
  }

  let next = today.find((item) => item.seconds > nowSeconds);
  if (!next) {
    const nextFajr = nextDayPrayerTimes?.fajr || prayerTimes.fajr;
    const nextFajrSeconds = toSeconds(nextFajr);
    if (nextFajrSeconds !== null) {
      next = { name: "fajr", seconds: nextFajrSeconds + 86400, rawTime: nextFajr };
    }
  }

  return {
    prayer: next?.name || "",
    mode: "until",
    duration: next ? formatDuration(next.seconds - nowSeconds) : "--:--:--",
    scheduledTime: formatPrayerClock(next?.rawTime),
    scheduledTimePeriod: formatPrayerClockPeriod(next?.rawTime),
    scene,
    activePrayer: recent?.name || "",
  };
};
